/**
 * PB-CDP-7 — the acknowledgement a user gives before a dissolve forfeits
 * purchased event plans or balances: the required tick at the bottom of the
 * amber losses notice in the three dissolving dialogs (an organisation
 * deleted outright, a solo owner's account taking its organisation with it,
 * the bell's company switch of a solo owner).
 *
 * Wire contract (main-backend `DissolveLossesAck`, mirror of
 * `ImportLegalBasis`): optional query params next to `acknowledgeLosses=true`
 * on the request AND the confirm leg of each door —
 * `lossesAckTextKey`, `lossesAckTextVersion`, `lossesAckLocale`,
 * `lossesAckShown`. The backend writes ONE `DISSOLVE_LOSSES_ACKNOWLEDGED`
 * audit row per acknowledged dissolve: outcome TICKED only when
 * `lossesAckTextKey` is the allowlisted key, else NOTICE_SHOWN. A malformed
 * value never refuses — it is recorded as "". A well-formed `P<n>B<b>` that
 * understates the losses the server finds at the confirm (more paid-plan
 * events, or balances where B0 said none) is refused with 400
 * `lossesNotAcknowledged` (reload, read the notice) before the row and
 * before anything irreversible; `GENERIC` covers everything. It records the
 * KEY, the version token, the UI locale and which sentence was shown —
 * never the sentence itself.
 *
 * The cockpit sends them only with the tick: every dissolving door is locked
 * until the box is ticked, so a dissolve with losses from this bundle always
 * carries them. Without a notice nothing is sent at all.
 */

/** i18n key of the tick's label (`userSettings.dissolveLosses.ackLabel`). */
export const DISSOLVE_LOSSES_ACK_TEXT_KEY = "userSettings.dissolveLosses.ackLabel";

/**
 * Version token of the label (backend shape `[A-Za-z0-9._-]{1,40}`). A change
 * of wording in any locale gets a new token here — the i18n test pins a hash
 * of every locale's label for v1 (`dissolveAck.i18n.test.ts`).
 */
export const DISSOLVE_LOSSES_ACK_TEXT_VERSION = "dissolve-losses-ack-v1";

/** What a ticked dialog sends, built at the click. */
export type LossesAck = {
  textKey: string;
  textVersion: string;
  /** BCP-47 tag of the UI language the label was shown in (`useLocale()`). */
  locale: string;
  /**
   * Which losses sentence the dialog showed: `P<n>B<0|1>` for the specific
   * one (n paid-plan events, B1 = the balances sentence) or `GENERIC`
   * (`describeDissolve`'s `shown`, from the branch that picked the sentence).
   */
  shown: string;
};

/** The acknowledgement for the current UI locale and the sentence shown. */
export function lossesAck(locale: string, shown: string): LossesAck {
  return {
    textKey: DISSOLVE_LOSSES_ACK_TEXT_KEY,
    textVersion: DISSOLVE_LOSSES_ACK_TEXT_VERSION,
    locale,
    shown,
  };
}

/**
 * The query params of an acknowledged dissolve — `acknowledgeLosses=true`
 * first, then the four `lossesAck*` — or none at all without one. Entries,
 * not a string: each client joins them to its own URL with its own `?` / `&`
 * (`lossesAckQuery`), and `URLSearchParams` encodes them.
 */
export function lossesAckParams(ack: LossesAck | null | undefined): [string, string][] {
  if (!ack) return [];
  return [
    ["acknowledgeLosses", "true"],
    ["lossesAckTextKey", ack.textKey],
    ["lossesAckTextVersion", ack.textVersion],
    ["lossesAckLocale", ack.locale],
    ["lossesAckShown", ack.shown],
  ];
}

/**
 * {@link lossesAckParams} as a query suffix led by `lead` (`?` for a URL
 * without a query yet, `&` after one), or "" without an acknowledgement.
 */
export function lossesAckQuery(ack: LossesAck | null | undefined, lead: "?" | "&"): string {
  const query = new URLSearchParams(lossesAckParams(ack)).toString();
  return query ? `${lead}${query}` : "";
}
